import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { fetch } from '@nrwl/angular';
import {
    catchError,
    debounceTime,
    map,
    mergeMap,
    switchMap,
    withLatestFrom,
} from 'rxjs/operators';
import { of } from 'rxjs';
import { select, Store } from '@ngrx/store';

import * as NotificationsActions from './notifications.actions';
import * as NotificationsFeature from './notifications.reducer';
import * as NotificationsSelectors from './notifications.selectors';
import { PortalsNotificationsService } from '../services/portals-notifications.service';

@Injectable()
export class NotificationsEffects {
    init$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.init),
            fetch({
                run: action => {
                    // Your custom service 'load' logic goes here. For now just dispatch a success action...
                    return NotificationsActions.loadNotifications();
                },
                onError: (action, error) => {
                    console.error(error);
                    return NotificationsActions.loadNotificationsFailure({
                        error,
                    });
                },
            }),
        ),
    );

    loadNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.loadNotifications),
            mergeMap(() =>
                this.notificationsService.getNotifications().pipe(
                    map(notifications =>
                        NotificationsActions.loadNotificationsSuccess({
                            notifications,
                        }),
                    ),
                    catchError(error =>
                        of(
                            NotificationsActions.loadNotificationsFailure({
                                error,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    loadMoreNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.loadMoreNotifications),
            withLatestFrom(
                this.store.pipe(select(NotificationsSelectors.getLoadedPage)),
            ),
            mergeMap(([, loadedPage]) =>
                this.notificationsService
                    .getNotifications((loadedPage ?? 0) + 1)
                    .pipe(
                        map(notifications =>
                            NotificationsActions.loadNotificationsSuccess({
                                notifications,
                            }),
                        ),
                        catchError(error =>
                            of(
                                NotificationsActions.loadNotificationsFailure({
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    markAsRead$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.markNotificationAsRead),
            mergeMap(({ notification }) =>
                this.notificationsService.markAsRead(notification.id).pipe(
                    map(() =>
                        NotificationsActions.markNotificationAsReadSuccess({
                            notification: {
                                id: notification.id,
                                changes: { read: true },
                            },
                        }),
                    ),
                    catchError(error =>
                        of(
                            NotificationsActions.markNotificationAsReadFailure(
                                {
                                    error,
                                },
                            ),
                        ),
                    ),
                ),
            ),
        ),
    );

    markAllAsRead$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.markAllNotificationsAsRead),
            withLatestFrom(
                this.store.pipe(
                    select(NotificationsSelectors.getAllNotifications),
                ),
            ),
            mergeMap(([, notifications]) =>
                this.notificationsService.markAllAsRead().pipe(
                    map(() => {
                        const updates = notifications
                            .filter(n => !n.read)
                            .map(n => ({
                                id: n.id,
                                changes: {
                                    read: true,
                                },
                            }));
                        return NotificationsActions.markAllNotificationsAsReadSuccess(
                            {
                                notifications: updates,
                            },
                        );
                    }),
                    catchError(error =>
                        of(
                            NotificationsActions.markAllNotificationsAsReadFailure(
                                {
                                    error,
                                },
                            ),
                        ),
                    ),
                ),
            ),
        ),
    );

    constructor(
        private readonly actions$: Actions,
        private readonly notificationsService: PortalsNotificationsService,
        private readonly store: Store<NotificationsFeature.State>,
    ) {}
}
